import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import {
  startGalleraOutboundCall,
  normalizeBrazilianPhone,
  voiceErrorMessage,
  ensureAudioUnlocked
} from "../../lib/galleraVoice";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";

/**
 * gallera-voice — BOTÃO FLUTUANTE (estilo Wavoip) para FAZER chamadas.
 *
 * Espelha o WebphoneFab do Wavoip (FAB arrastável no canto), mas apontando para o
 * NOSSO motor de voz. Ao clicar, abre um mini-teclado: escolhe a conexão de voz,
 * digita o número e liga. A chamada em si (áudio WebRTC contra o motor) é tratada
 * pelo overlay global GalleraVoiceCall — este FAB só ORIGINA a chamada via
 * startGalleraOutboundCall (que dispara o evento galleraVoice:startCall). Assim o
 * peso todo (mídia/WebRTC/motor) fica fora do caminho da mensageria.
 *
 * Auto-gated: só aparece se GET /voice/enabled = true (plano useVoice + serviço no
 * servidor). Zero impacto na mensageria: nada síncrono, carrega sob demanda.
 *
 * O FAB de RECEBER já é o overlay GalleraVoiceCall; este é só o de ORIGINAR, que é
 * o que faltava para o fluxo "atender e ligar tudo pelo app10, estilo Wavoip".
 */

const POS_KEY = "gallera_voice_fab_position";

const styles = {
  fab: {
    position: "fixed",
    zIndex: 9997, // logo abaixo do WebphoneFab (9998) p/ não sobrepor o do Wavoip
    cursor: "grab",
  },
  button: {
    width: 45,
    height: 45,
    borderRadius: "50%",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#25D366", // verde "ligar" (distingue do azul Wavoip)
    color: "#fff",
    boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
  },
  panel: {
    position: "fixed",
    zIndex: 12999, // abaixo dos cards de chamada (13000), acima do resto
    width: 260,
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    overflow: "hidden",
    fontFamily: "Arial, sans-serif",
  },
  panelHeader: {
    background: "#00339E",
    color: "#fff",
    padding: "12px 14px",
    fontWeight: "bold",
    fontSize: 14,
  },
  panelBody: { padding: 14 },
  label: { fontSize: 12, color: "#666", marginBottom: 4, display: "block" },
  input: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 15,
    marginBottom: 10,
    boxSizing: "border-box",
  },
  callBtn: {
    width: "100%",
    height: 44,
    border: "none",
    borderRadius: 8,
    background: "#25D366",
    color: "#fff",
    fontSize: 15,
    fontWeight: "bold",
    cursor: "pointer",
  },
  callBtnDisabled: { background: "#9bd9b0", cursor: "not-allowed" },
  empty: { fontSize: 13, color: "#888", padding: "6px 0" },
  preview: { fontSize: 12, marginTop: -6, marginBottom: 10, minHeight: 15 },
  previewOk: { color: "#1a7f37", fontWeight: "bold" },
  previewErr: { color: "#c0392b" },
};

const GalleraVoiceFab = () => {
  const ctx = useContext(WhatsAppsContext);
  const ctxWhatsApps = ctx?.whatsApps;
  const whatsApps = useMemo(() => ctxWhatsApps || [], [ctxWhatsApps]);

  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [calling, setCalling] = useState(false);

  // posição persistida (mesma lógica leve do WebphoneFab, sem exageros)
  const [pos, setPos] = useState(null);
  const draggingRef = useRef(false);
  const didDragRef = useRef(false);
  const offsetRef = useRef({ x: 0, y: 0 });

  // gate: plano + serviço de voz
  useEffect(() => {
    let alive = true;
    api
      .get("/voice/enabled")
      .then(({ data }) => { if (alive) setEnabled(!!data?.enabled); })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn("[GALLERA-VOICE] FAB: GET /voice/enabled falhou (discador oculto):", err?.response?.status || err?.message);
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(POS_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        if (p && typeof p.x === "number" && typeof p.y === "number") setPos(p);
      }
    } catch (_) { /* ignore */ }
  }, []);

  // Conexões APTAS a voz: voiceEnabled (motor local pareado) OU voiceToken (modelo por
  // token/.254). SEM fallback p/ "todas as conectadas" — antes o fallback listava
  // conexões sem voz e a ligação falhava depois de forma confusa. Se nenhuma tem voz,
  // o painel mostra o aviso explícito (nunca lista vazia silenciosa).
  const voiceConnections = useMemo(() => {
    return whatsApps.filter(
      (w) =>
        w &&
        (w.voiceEnabled ||
          (typeof w.voiceToken === "string" && w.voiceToken.trim() !== ""))
    );
  }, [whatsApps]);

  useEffect(() => {
    if (!selectedId && voiceConnections.length) setSelectedId(String(voiceConnections[0].id));
  }, [voiceConnections, selectedId]);

  // pré-visualização determinística do número que SERÁ discado (mostrada no modal)
  const normalized = useMemo(() => normalizeBrazilianPhone(number), [number]);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    const el = document.getElementById("gallera_voice_fab");
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!pos) setPos({ x: rect.left, y: rect.top });
    draggingRef.current = true;
    didDragRef.current = false;
    offsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const el = document.getElementById("gallera_voice_fab");
      if (!el) return;
      const maxX = window.innerWidth - el.offsetWidth;
      const maxY = window.innerHeight - el.offsetHeight;
      let x = Math.max(0, Math.min(e.clientX - offsetRef.current.x, maxX));
      let y = Math.max(0, Math.min(e.clientY - offsetRef.current.y, maxY));
      setPos({ x, y });
      didDragRef.current = true;
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setPos((p) => {
        if (p) { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch (_) {} }
        return p;
      });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, []);

  // TODO caminho de falha aqui TOASTA + LOGA ([GALLERA-VOICE]) — nunca falha muda.
  // Evidência de produção (2026-07-06): clique em "Ligar" sem NENHUM POST no nginx =
  // falha silenciosa antes do fetch; por isso cada early-return abaixo é barulhento.
  const handleCall = async () => {
    // DESTRAVA o áudio SOB O GESTO do clique (síncrono, antes de qualquer await):
    // o áudio da chamada abre depois num useEffect fora do gesto — sem isto o
    // AudioContext ficava "suspended" no OUTBOUND e não vinha som.
    ensureAudioUnlocked();
    // eslint-disable-next-line no-console
    console.log("[GALLERA-VOICE] FAB: clique em Ligar", {
      entrada: number,
      selectedId,
      conexoesComVoz: voiceConnections.length
    });
    if (calling) {
      // eslint-disable-next-line no-console
      console.warn("[GALLERA-VOICE] FAB: já existe uma originação em andamento (clique ignorado)");
      return;
    }
    const norm = normalizeBrazilianPhone(number);
    if (!norm.ok) {
      // eslint-disable-next-line no-console
      console.error("[GALLERA-VOICE] FAB: número reprovado na normalização:", number, "→", norm.message);
      toast.error(norm.message || "Digite DDD+número.");
      return;
    }
    if (!selectedId) {
      // eslint-disable-next-line no-console
      console.error("[GALLERA-VOICE] FAB: nenhuma conexão de voz selecionada");
      toast.error(
        voiceConnections.length === 0
          ? "Nenhuma conexão com voz configurada — ative a Gallera Voz numa conexão em Conexões."
          : "Selecione a conexão de voz."
      );
      return;
    }
    const conn = voiceConnections.find((w) => String(w.id) === String(selectedId));
    if (!conn) {
      // eslint-disable-next-line no-console
      console.error("[GALLERA-VOICE] FAB: conexão selecionada não está mais apta a voz:", selectedId);
      toast.error("A conexão selecionada não tem mais voz habilitada — selecione outra.");
      setSelectedId("");
      return;
    }
    setCalling(true);
    try {
      await startGalleraOutboundCall(Number(selectedId), norm.number, {
        whatsappName: conn.name || ""
      });
      setOpen(false);
      setNumber("");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[GALLERA-VOICE] FAB: falha ao originar:", err);
      toast.error(voiceErrorMessage(err));
    } finally {
      setCalling(false);
    }
  };

  if (!enabled) return null;

  const fabStyle = pos
    ? { ...styles.fab, left: pos.x, top: pos.y }
    : { ...styles.fab, right: 25, bottom: 140 }; // acima do WebphoneFab por padrão

  // painel ancorado logo acima do FAB
  const panelStyle = pos
    ? { ...styles.panel, left: Math.min(pos.x, window.innerWidth - 270), top: Math.max(pos.y - 240, 10) }
    : { ...styles.panel, right: 25, bottom: 195 };

  return (
    <>
      {open && (
        <div style={panelStyle}>
          <div style={styles.panelHeader}>📞 Ligar — Gallera Voz</div>
          <div style={styles.panelBody}>
            {voiceConnections.length === 0 ? (
              <div style={styles.empty}>
                Nenhuma conexão com voz configurada. Ative a Gallera Voz (token) numa
                conexão em Conexões.
              </div>
            ) : (
              <>
                <label style={styles.label}>Conexão de voz</label>
                <select
                  style={styles.input}
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {voiceConnections.map((w) => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <label style={styles.label}>Número (DDD + número)</label>
                <input
                  style={styles.input}
                  type="tel"
                  placeholder="Ex.: 18 99999-9999"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCall(); }}
                  autoFocus
                />
                {/* número FINAL que será discado (normalizado) ou o motivo do bloqueio */}
                <div style={styles.preview}>
                  {String(number || "").trim() === "" ? null : normalized.ok ? (
                    <span style={styles.previewOk}>Vai discar: +{normalized.number}</span>
                  ) : (
                    <span style={styles.previewErr}>{normalized.message}</span>
                  )}
                </div>
                <button
                  type="button"
                  style={{
                    ...styles.callBtn,
                    ...(calling || !normalized.ok ? styles.callBtnDisabled : {})
                  }}
                  onClick={handleCall}
                  disabled={calling}
                >
                  {calling ? "Chamando…" : "Ligar"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div id="gallera_voice_fab" style={fabStyle} onMouseDown={onMouseDown} title="Ligar (Gallera Voz)">
        <button
          type="button"
          style={styles.button}
          onClick={() => {
            if (didDragRef.current) { didDragRef.current = false; return; }
            setOpen((v) => !v);
          }}
          aria-label="Ligar pela Gallera Voz"
        >
          <span style={{ fontSize: 20 }}>📞</span>
        </button>
      </div>
    </>
  );
};

export default GalleraVoiceFab;
